import type {
    ChangeEvent,
    ComponentProps,
    FC,
    FormEvent,
    ReactNode
} from 'react'
import type { InquiryErrors, InquiryField, InquiryInput } from '@/lib/inquiry'

import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Button, ButtonLink } from '@/components/generals/button'
import { SERVICES } from '@/constants/services'

import {
    ArrowRight,
    ChevronDown,
    CircleAlert,
    CircleCheck,
    LoaderCircle
} from 'lucide-react'
import {
    EMPTY_INQUIRY,
    INQUIRY_FIELD_ORDER,
    INQUIRY_LIMITS,
    normalizeInquiry,
    validateInquiry
} from '@/lib/inquiry'
import {
    BUDGET_OPTIONS,
    EVENT_OPTIONS,
    GUEST_COUNT_OPTIONS,
    HEARD_FROM_OPTIONS,
    SERVICE_OPTIONS
} from '@/constants/inquiry'

type Status = 'idle' | 'submitting' | 'success' | 'error'

type TextField = Exclude<InquiryField, 'events' | 'datesFlexible'>

type InquiryResponse = {
    ok?: boolean
    message?: string
    errors?: InquiryErrors
}

const INPUT_CLASS = 'block w-full rounded-xl border border-input bg-background px-4 py-3 text-base text-foreground shadow-[inset_0_1px_2px_rgba(43,21,24,0.05)] transition-colors placeholder:text-muted-foreground/70 hover:border-primary/30 focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-ring/40 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus:ring-destructive/25'

const fieldId = (field: InquiryField): string => `inquiry-${field}`

const localToday = (): string => {
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')

    return `${now.getFullYear()}-${month}-${day}`
}

interface FieldProps {
    field: InquiryField
    label: string
    optional?: boolean
    hint?: string
    error?: string
    className?: string
    children: ReactNode
}

const Field: FC<FieldProps> = ({ field, label, optional, hint, error, className, children }): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={fieldId(field)} className='flex items-baseline justify-between gap-3 text-sm font-medium text-foreground'>
                {label}
                {optional && <span className='text-xs font-normal text-muted-foreground'>Optional</span>}
            </label>

            <div className='mt-2'>
                {children}
            </div>

            {hint && !error && (
                <p id={`${fieldId(field)}-hint`} className='mt-2 text-sm text-muted-foreground'>
                    {hint}
                </p>
            )}

            {error && (
                <p id={`${fieldId(field)}-error`} className='mt-2 flex items-start gap-1.5 text-sm text-destructive'>
                    <CircleAlert aria-hidden='true' className='mt-0.5 size-4 shrink-0' />
                    {error}
                </p>
            )}
        </div>
    )
}

const SelectInput: FC<ComponentProps<'select'>> = ({ className, children, ...props }): ReactNode => {
    return (
        <div className='relative'>
            <select className={cn(INPUT_CLASS, 'cursor-pointer appearance-none pr-11', className)} {...props}>
                {children}
            </select>

            <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
        </div>
    )
}

export const InquiryForm: FC = (): ReactNode => {
    const router = useRouter()
    const [values, setValues] = useState<InquiryInput>(EMPTY_INQUIRY)
    const [errors, setErrors] = useState<InquiryErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const [today, setToday] = useState('')
    const [honeypot, setHoneypot] = useState('')
    const [firstName, setFirstName] = useState('')
    const successRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setToday(localToday())
    }, [])

    useEffect(() => {
        if (!router.isReady) return

        const requested = typeof router.query.service === 'string' ? router.query.service : ''
        const match = SERVICES.find(service => service.id === requested)

        if (!match) return

        setValues(current => current.service ? current : { ...current, service: match.name })
    }, [router.isReady, router.query.service])

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const clearErrors = (...fields: Array<InquiryField>) => {
        setErrors(current => {
            if (!fields.some(field => current[field])) return current

            const next = { ...current }

            for (const field of fields) delete next[field]

            return next
        })
    }

    const setText = (field: TextField) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))
        clearErrors(field)
    }

    const toggleFlexible = (event: ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target

        setValues(current => ({ ...current, datesFlexible: checked }))
        clearErrors('weddingDate')
    }

    const toggleEvent = (option: string) => {
        setValues(current => ({
            ...current,
            events: current.events.includes(option)
                ? current.events.filter(event => event !== option)
                : [...current.events, option]
        }))
    }

    const describedBy = (field: InquiryField, hasHint = false): string | undefined => {
        if (errors[field]) return `${fieldId(field)}-error`
        if (hasHint) return `${fieldId(field)}-hint`

        return undefined
    }

    const focusFirstError = (found: InquiryErrors) => {
        const first = INQUIRY_FIELD_ORDER.find(field => found[field])

        if (!first) return

        const element = first === 'events'
            ? document.querySelector<HTMLElement>(`#${fieldId('events')} input`)
            : document.getElementById(fieldId(first))

        element?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        const input = normalizeInquiry(values)
        const found = validateInquiry(input, today || localToday())

        if (Object.keys(found).length > 0) {
            setErrors(found)
            setMessage('')
            setStatus('idle')
            focusFirstError(found)

            return
        }

        setErrors({})
        setMessage('')
        setStatus('submitting')

        try {
            const response = await fetch('/api/inquiries', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...input, website: honeypot })
            })

            const data = await response.json().catch(() => null) as InquiryResponse | null

            if (response.ok && data?.ok) {
                setFirstName(input.name.split(' ')[0] ?? '')
                setValues(EMPTY_INQUIRY)
                setStatus('success')

                return
            }

            if (data?.errors && Object.keys(data.errors).length > 0) {
                setErrors(data.errors)
                focusFirstError(data.errors)
            }

            setMessage(data?.message ?? "Something went wrong while sending your inquiry. Please try again.")
            setStatus('error')
        }
        catch {
            setMessage("We could not reach the studio just now. Please check your connection and try again.")
            setStatus('error')
        }
    }

    if (status === 'success') {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className='rounded-[2rem] border border-border bg-card p-8 text-center shadow-[0_40px_80px_-60px_rgba(74,13,24,0.6)] focus:outline-none sm:p-12'
            >
                <span className='mx-auto flex size-16 items-center justify-center rounded-full bg-henna/10 text-henna'>
                    <CircleCheck aria-hidden='true' className='size-8' />
                </span>

                <h2 className='mt-6 text-4xl text-foreground'>
                    {firstName ? `Thank you, ${firstName}` : "Thank you"}
                </h2>

                <p className='mx-auto mt-4 max-w-md text-lg leading-relaxed text-muted-foreground'>
                    Your inquiry is with our planners. We will reply within two working days with a few questions and a time for your free consultation.
                </p>

                <div className='mt-8 flex flex-col justify-center gap-3 sm:flex-row'>
                    <ButtonLink href='/weddings'>
                        See real weddings
                        <ArrowRight aria-hidden='true' />
                    </ButtonLink>

                    <Button variant='outline' onClick={() => setStatus('idle')}>
                        Send another inquiry
                    </Button>
                </div>
            </div>
        )
    }

    const submitting = status === 'submitting'

    return (
        <form
            noValidate
            onSubmit={handleSubmit}
            aria-labelledby='inquiry-form-title'
            className='relative rounded-[2rem] border border-border bg-card p-6 shadow-[0_40px_80px_-60px_rgba(74,13,24,0.6)] sm:p-10'
        >
            <h2 id='inquiry-form-title' className='text-3xl text-foreground sm:text-4xl'>
                Your wedding, in a few lines
            </h2>

            <p className='mt-2 leading-relaxed text-muted-foreground'>
                It takes about three minutes. Only your name, email, date and guest count are needed.
            </p>

            {status === 'error' && message && (
                <div role='alert' className='mt-6 flex gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive'>
                    <CircleAlert aria-hidden='true' className='mt-0.5 size-4 shrink-0' />
                    <p>{message}</p>
                </div>
            )}

            <fieldset className='mt-8'>
                <legend className='font-heading text-2xl text-foreground'>
                    About you
                </legend>

                <div className='mt-5 grid gap-5 sm:grid-cols-2'>
                    <Field field='name' label="Your name" error={errors.name}>
                        <input
                            id={fieldId('name')}
                            type='text'
                            autoComplete='name'
                            required
                            maxLength={INQUIRY_LIMITS.name}
                            value={values.name}
                            onChange={setText('name')}
                            aria-invalid={Boolean(errors.name)}
                            aria-describedby={describedBy('name')}
                            className={INPUT_CLASS}
                        />
                    </Field>

                    <Field field='partnerName' label="Your partner's name" optional error={errors.partnerName}>
                        <input
                            id={fieldId('partnerName')}
                            type='text'
                            autoComplete='off'
                            maxLength={INQUIRY_LIMITS.partnerName}
                            value={values.partnerName}
                            onChange={setText('partnerName')}
                            aria-invalid={Boolean(errors.partnerName)}
                            aria-describedby={describedBy('partnerName')}
                            className={INPUT_CLASS}
                        />
                    </Field>

                    <Field field='email' label="Email" error={errors.email}>
                        <input
                            id={fieldId('email')}
                            type='email'
                            inputMode='email'
                            autoComplete='email'
                            required
                            maxLength={INQUIRY_LIMITS.email}
                            value={values.email}
                            onChange={setText('email')}
                            aria-invalid={Boolean(errors.email)}
                            aria-describedby={describedBy('email')}
                            className={INPUT_CLASS}
                        />
                    </Field>

                    <Field field='phone' label="Phone or WhatsApp" optional hint="Include the country code if you live outside India." error={errors.phone}>
                        <input
                            id={fieldId('phone')}
                            type='tel'
                            inputMode='tel'
                            autoComplete='tel'
                            maxLength={INQUIRY_LIMITS.phone}
                            value={values.phone}
                            onChange={setText('phone')}
                            aria-invalid={Boolean(errors.phone)}
                            aria-describedby={describedBy('phone', true)}
                            className={INPUT_CLASS}
                        />
                    </Field>
                </div>
            </fieldset>

            <div aria-hidden='true' className='my-10 h-px bg-border' />

            <fieldset>
                <legend className='font-heading text-2xl text-foreground'>
                    The wedding
                </legend>

                <div className='mt-5 grid gap-5 sm:grid-cols-2'>
                    <Field
                        field='weddingDate'
                        label="Wedding date"
                        optional={values.datesFlexible}
                        hint="An approximate date is fine. It helps us check venues."
                        error={errors.weddingDate}
                    >
                        <input
                            id={fieldId('weddingDate')}
                            type='date'
                            min={today || undefined}
                            required={!values.datesFlexible}
                            value={values.weddingDate}
                            onChange={setText('weddingDate')}
                            aria-invalid={Boolean(errors.weddingDate)}
                            aria-describedby={describedBy('weddingDate', true)}
                            className={INPUT_CLASS}
                        />

                        <label className='mt-3 inline-flex cursor-pointer items-center gap-2.5 text-sm text-foreground/85'>
                            <input
                                type='checkbox'
                                checked={values.datesFlexible}
                                onChange={toggleFlexible}
                                className='size-4 cursor-pointer accent-primary'
                            />
                            Our dates are not fixed yet
                        </label>
                    </Field>

                    <Field field='location' label="Where are you thinking?" optional error={errors.location}>
                        <input
                            id={fieldId('location')}
                            type='text'
                            maxLength={INQUIRY_LIMITS.location}
                            placeholder="Udaipur, Goa, abroad or not sure yet"
                            value={values.location}
                            onChange={setText('location')}
                            aria-invalid={Boolean(errors.location)}
                            aria-describedby={describedBy('location')}
                            className={INPUT_CLASS}
                        />
                    </Field>

                    <Field field='guestCount' label="Guest count" error={errors.guestCount}>
                        <SelectInput
                            id={fieldId('guestCount')}
                            required
                            value={values.guestCount}
                            onChange={setText('guestCount')}
                            aria-invalid={Boolean(errors.guestCount)}
                            aria-describedby={describedBy('guestCount')}
                        >
                            <option value='' disabled>
                                Choose a range
                            </option>

                            {GUEST_COUNT_OPTIONS.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </SelectInput>
                    </Field>

                    <Field field='budget' label="Overall budget" optional error={errors.budget}>
                        <SelectInput
                            id={fieldId('budget')}
                            value={values.budget}
                            onChange={setText('budget')}
                            aria-invalid={Boolean(errors.budget)}
                            aria-describedby={describedBy('budget')}
                        >
                            <option value=''>
                                Choose a range
                            </option>

                            {BUDGET_OPTIONS.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </SelectInput>
                    </Field>

                    <Field field='service' label="How can we help?" optional error={errors.service} className='sm:col-span-2'>
                        <SelectInput
                            id={fieldId('service')}
                            value={values.service}
                            onChange={setText('service')}
                            aria-invalid={Boolean(errors.service)}
                            aria-describedby={describedBy('service')}
                        >
                            <option value=''>
                                Choose a service
                            </option>

                            {SERVICE_OPTIONS.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </SelectInput>
                    </Field>
                </div>

                <fieldset id={fieldId('events')} className='mt-6'>
                    <legend className='flex w-full items-baseline justify-between gap-3 text-sm font-medium text-foreground'>
                        Functions you are planning
                        <span className='text-xs font-normal text-muted-foreground'>Optional</span>
                    </legend>

                    <div className='mt-3 flex flex-wrap gap-2'>
                        {EVENT_OPTIONS.map(option => {
                            const checked = values.events.includes(option)

                            return (
                                <label
                                    key={option}
                                    className={cn(
                                        'cursor-pointer select-none rounded-full border px-4 py-2 text-sm transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
                                        checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background text-foreground/85 hover:border-primary/40'
                                    )}
                                >
                                    <input
                                        type='checkbox'
                                        name='events'
                                        value={option}
                                        checked={checked}
                                        onChange={() => toggleEvent(option)}
                                        className='sr-only'
                                    />
                                    {option}
                                </label>
                            )
                        })}
                    </div>
                </fieldset>
            </fieldset>

            <div aria-hidden='true' className='my-10 h-px bg-border' />

            <fieldset>
                <legend className='font-heading text-2xl text-foreground'>
                    In your words
                </legend>

                <div className='mt-5 grid gap-5'>
                    <Field field='message' label="Tell us about your wedding" optional error={errors.message}>
                        <textarea
                            id={fieldId('message')}
                            rows={5}
                            maxLength={INQUIRY_LIMITS.message}
                            placeholder="Your families, your traditions, the feeling you want, anything at all."
                            value={values.message}
                            onChange={setText('message')}
                            aria-invalid={Boolean(errors.message)}
                            aria-describedby={describedBy('message')}
                            className={cn(INPUT_CLASS, 'min-h-36 resize-y')}
                        />

                        <p aria-hidden='true' className='mt-1.5 text-right text-xs text-muted-foreground'>
                            {values.message.length} / {INQUIRY_LIMITS.message}
                        </p>
                    </Field>

                    <Field field='heardFrom' label="How did you hear about us?" optional error={errors.heardFrom}>
                        <SelectInput
                            id={fieldId('heardFrom')}
                            value={values.heardFrom}
                            onChange={setText('heardFrom')}
                            aria-invalid={Boolean(errors.heardFrom)}
                            aria-describedby={describedBy('heardFrom')}
                        >
                            <option value=''>
                                Choose one
                            </option>

                            {HEARD_FROM_OPTIONS.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </SelectInput>
                    </Field>
                </div>
            </fieldset>

            <div aria-hidden='true' className='absolute -left-[9999px] top-auto size-px overflow-hidden'>
                <label htmlFor='inquiry-website'>
                    Leave this field empty
                </label>

                <input
                    id='inquiry-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={honeypot}
                    onChange={event => setHoneypot(event.target.value)}
                />
            </div>

            <div className='mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-xs text-sm leading-relaxed text-muted-foreground'>
                    We only use your details to reply about your wedding.
                </p>

                <Button type='submit' size='lg' disabled={submitting} aria-busy={submitting}>
                    {submitting ? (
                        <>
                            <LoaderCircle aria-hidden='true' className='animate-spin' />
                            Sending your inquiry
                        </>
                    ) : (
                        <>
                            Send my inquiry
                            <ArrowRight aria-hidden='true' />
                        </>
                    )}
                </Button>
            </div>
        </form>
    )
}
